import { createStyles, TextInput, type TextInputProps } from '@mantine/core';
import { useState } from 'react';
import { ASSETS_BUCKET_URL } from '@/config';

type InputRegister = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  error?: string | undefined;
};

type InputFieldProps = TextInputProps & {
  label: string | React.ReactNode;
  register: InputRegister;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel';
  className?: string;
  checkregex?: string;
  isnumber?: string;
};

const useStyles = createStyles((theme, { floating }: { floating: boolean }) => ({
  root: {
    position: 'relative',
  },

  label: {
    position: 'absolute',
    zIndex: 2,
    top: 18,
    left: theme.spacing.md,
    pointerEvents: 'none',
    // eslint-disable-next-line no-nested-ternary
    color: floating
      ? theme.colorScheme === 'dark'
        ? theme.white
        : theme.colors.gray[6]
      : theme.colorScheme === 'dark'
      ? theme.colors.dark[3]
      : theme.colors.gray[5],
    transition: 'transform 150ms ease, color 150ms ease, font-size 150ms ease',
    transform: floating ? 'translate(0, -12px)' : 'none',
    fontSize: floating ? theme.fontSizes.md : theme.fontSizes.lg,
    fontWeight: floating ? 400 : 600,
    lineHeight: 1.13,
    letterSpacing: '0.19px',
  },

  required: {
    transition: 'opacity 150ms ease',
    opacity: floating ? 1 : 0,
  },

  input: {
    border: 'none',
    backgroundColor: '#f7f7f7',
    outline: 'none',
    height: 54,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.13,
    letterSpacing: '0.19px',
    borderRadius: '8px',
    color: '#212121 !important',
    paddingTop: theme.spacing.md,
    paddingLeft: theme.spacing.md,

    '&::placeholder': {
      transition: 'color 150ms ease',
      color: !floating ? 'transparent' : undefined,
    },
  },
  rightSection: {
    right: theme.spacing.xs,
    // if the input has value, show the clear icon or else hide it
    opacity: floating ? 1 : 0,
  },
}));

export const InputField = (props: InputFieldProps) => {
  const { type, label, register, checkregex, isnumber } = props;
  // eslint-disable-next-line no-useless-escape
  const regex = /[~`!@#$%^&()_={}[\]:;,.<>+\/?-]/;

  const [focused, setFocused] = useState(false);

  const { classes } = useStyles({ floating: register.value?.toString()?.length !== 0 || focused });
  const { value, error, onChange, onBlur, onFocus } = register;
  return (
    <TextInput
      {...props}
      label={label}
      type={type}
      classNames={classes}
      className={props.className}
      onBlur={() => {
        setTimeout(() => {
          setFocused(false);
          onBlur?.();
        }, 100);
      }}
      error={error}
      value={value}
      onFocus={() => {
        setFocused(true);
        onFocus?.();
      }}
      onChange={(e) => {
        if (isnumber) {
          if (e.currentTarget.value?.length > 0 && Number.isNaN(Number(e.currentTarget.value))) {
            return;
          }
        }
        if (checkregex && regex.test(e.currentTarget.value)) {
          return;
        }
        if (e.currentTarget.value?.length > 50) {
          onChange(e.currentTarget.value?.slice(0, 50));
          return;
        }
        onChange(e.currentTarget.value);
      }}
      onKeyUp={(e: any) => {
        if (e.key === 'Enter') {
          e.target.blur();
        }
      }}
      rightSection={
        value?.length !== 0 &&
        focused && (
          <img
            src={`${ASSETS_BUCKET_URL}/svg/Clear.svg`}
            alt="Clear"
            loading="lazy"
            className="text-field-icon pointer"
            onClick={() => {
              onChange('');
              onBlur?.();
              setFocused(false);
            }}
          />
        )
      }
    />
  );
};
